import { initDictionaryDeleteFlow } from './hardware-platforms/delete-flow.js';
import { initDictionaryFeedback } from './hardware-platforms/feedback-controller.js';
import { carryDictionaryDrafts, initDictionaryRows } from './hardware-platforms/row-controller.js';
import {
    createDictionarySearchController,
    DIRTY_SEARCH_FEEDBACK,
} from './hardware-platforms/search-controller.js';
import { initStatusColorPreview } from './hardware-platforms/status-color-preview.js';
import { initStatusSection } from './hardware-platforms/status-section.js';

export function initHardwarePlatforms(root = document, windowRef = window, fetchImpl = globalThis.fetch) {
    const page = root.querySelector?.('[data-page-view="hardware-platforms"]');
    if (!(page instanceof HTMLElement)) {
        return null;
    }

    const searchControllers = new WeakMap();
    const initSearchController = (section) => {
        const controller = createDictionarySearchController({
            section,
            fetchImpl,
            windowRef,
        });
        if (controller !== null) {
            searchControllers.set(section, controller);
        }
    };
    root.querySelectorAll('[data-dictionary-section]').forEach(initSearchController);

    const dictionaryFeedback = initDictionaryFeedback(root, windowRef);
    const refreshSection = async (requestedSection, replacementSection) => {
        // An earlier refresh may have swapped the whole section since this request was sent.
        const type = String(requestedSection.dataset?.dictionaryType ?? '');
        const currentSection = requestedSection.isConnected
            ? requestedSection
            : root.querySelector(`[data-dictionary-section][data-dictionary-type="${type}"]`) ?? requestedSection;
        const searchController = searchControllers.get(currentSection);
        let committedSection = currentSection;
        if (!(await searchController?.refreshResults?.(replacementSection))) {
            searchController?.cancel?.('');
            carryDictionaryDrafts(currentSection, replacementSection);
            currentSection.replaceWith(replacementSection);
            initSearchController(replacementSection);
            committedSection = replacementSection;
        }
        // The refresh cleared the search notice; a draft carried over still blocks searching.
        if (committedSection.querySelector('[data-dictionary-state="dirty"]') !== null) {
            searchControllers.get(committedSection)?.cancel?.(DIRTY_SEARCH_FEEDBACK);
        }

        return committedSection;
    };
    // One refresh at a time: two overlapping ones cancel each other's swap, and the loser would fall back to
    // replacing a section the winner has already taken off the page.
    let refreshQueue = Promise.resolve();
    const commitSectionRefresh = (requestedSection, replacementSection) => {
        const refresh = refreshQueue.then(() => refreshSection(requestedSection, replacementSection));
        refreshQueue = refresh.catch(() => {});

        return refresh;
    };
    initDictionaryRows({
        root,
        windowRef,
        fetchImpl,
        commitRefresh: commitSectionRefresh,
        onSectionStateChange: (section, hasDirtyRows) => {
            const searchController = searchControllers.get(section);
            if (hasDirtyRows) {
                searchController?.cancel?.(DIRTY_SEARCH_FEEDBACK);
            } else {
                searchController?.clearFeedback?.(DIRTY_SEARCH_FEEDBACK);
            }
        },
    });
    initStatusColorPreview(root);
    const statusSection = initStatusSection({ root, windowRef, fetchImpl, dictionaryFeedback });
    const deleteFlow = initDictionaryDeleteFlow(
        root,
        windowRef,
        fetchImpl,
        commitSectionRefresh,
        dictionaryFeedback,
    );

    return {
        closeDeleteModal: () => deleteFlow?.close(),
        closeStatusDeleteModal: () => statusSection?.closeDeleteModal(),
    };
}

if (typeof document !== 'undefined') {
    initHardwarePlatforms();
}
